import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import GoogleIcon from '@mui/icons-material/Google';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LanguageIcon from '@mui/icons-material/Language';
import { Paths } from '@/types/routes';
import { SeoProject } from '@/types/api/seo';
import { GoogleConnectionStatus, SearchConsoleProperty } from '@/services/searchConsole';
import { GA4Property } from '@/services/googleAnalytics';

type Props = {
  project: SeoProject | null;
  connectionStatus: GoogleConnectionStatus | null;
  availableSites: SearchConsoleProperty[];
  ga4Properties: GA4Property[];
  isLoading: boolean;
  isLoadingGa4: boolean;
  isSaving: boolean;
  projectName: string;
  onProjectNameChange: (name: string) => void;
  selectedSite: string;
  onSelectedSiteChange: (siteUrl: string) => void;
  selectedGa4: string;
  onSelectedGa4Change: (propertyId: string) => void;
  onSave: () => void;
  onDelete: () => void;
  // WordPress
  wpSiteUrl: string;
  onWpSiteUrlChange: (url: string) => void;
  wpUsername: string;
  onWpUsernameChange: (username: string) => void;
  wpAppPassword: string;
  onWpAppPasswordChange: (password: string) => void;
  onConnectWordPress: () => void;
  onDisconnectWordPress: () => void;
  isConnectingWp: boolean;
  wpConnectionError: string | null;
  onSyncWpPages: () => void;
  isSyncingWpPages: boolean;
  wpPagesSynced: number | null;
};

const formatSiteUrl = (url: string): string =>
  url.replace('sc-domain:', '').replace(/^https?:\/\//, '').replace(/\/$/, '');

const SEOxProjectSettingsComponent = function ({
  project,
  connectionStatus,
  availableSites,
  ga4Properties,
  isLoading,
  isLoadingGa4,
  isSaving,
  projectName,
  onProjectNameChange,
  selectedSite,
  onSelectedSiteChange,
  selectedGa4,
  onSelectedGa4Change,
  onSave,
  onDelete,
  wpSiteUrl,
  onWpSiteUrlChange,
  wpUsername,
  onWpUsernameChange,
  wpAppPassword,
  onWpAppPasswordChange,
  onConnectWordPress,
  onDisconnectWordPress,
  isConnectingWp,
  wpConnectionError,
  onSyncWpPages,
  isSyncingWpPages,
  wpPagesSynced,
}: Props): JSX.Element {
  if (isLoading || !project) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#054AD9' }} />
      </Box>
    );
  }

  const sites = availableSites;
  const isConnected = connectionStatus?.isConnected;

  return (
    <Box sx={{}}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Project Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Configure this project&apos;s data sources and details.
      </Typography>

      {/* Project Name */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff', mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
          Project Name
        </Typography>
        <TextField
          fullWidth
          value={projectName}
          onChange={(e) => onProjectNameChange(e.target.value)}
          size="small"
        />
      </Box>

      {/* Google Connection Status */}
      {!isConnected && (
        <Alert severity="info" sx={{ mb: 3, fontSize: 13 }}>
          Google is not connected.{' '}
          <Link to={Paths.SETTINGS_INTEGRATIONS} style={{ fontWeight: 600 }}>
            Connect in Settings
          </Link>{' '}
          to select Search Console and GA4 data sources.
        </Alert>
      )}

      {/* Search Console Website */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <GoogleIcon sx={{ fontSize: 18, color: '#4285F4' }} />
          <Typography variant="subtitle2" fontWeight={700}>Search Console Website</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
          Select the site to track rankings, clicks, and impressions for this project.
        </Typography>

        {!isConnected || sites.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {isConnected ? 'No Search Console sites found.' : 'Connect Google first.'}
          </Typography>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <Select
                value={selectedSite}
                onChange={(e) => onSelectedSiteChange(e.target.value)}
                displayEmpty
                sx={{ fontSize: 14 }}
                size="small"
              >
                <MenuItem value="" disabled>Select a website...</MenuItem>
                {sites.map((site) => (
                  <MenuItem key={site.siteUrl} value={site.siteUrl} sx={{ fontSize: 13 }}>
                    {formatSiteUrl(site.siteUrl)}
                    <Chip label={site.permissionLevel.replace('site', '')} size="small" sx={{ fontSize: 10, height: 18, ml: 1 }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {project.siteUrl && selectedSite === project.siteUrl && (
              <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 14 }} /> Currently tracking {formatSiteUrl(project.siteUrl)}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* GA4 Property */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <AnalyticsIcon sx={{ fontSize: 18, color: '#F4B400' }} />
          <Typography variant="subtitle2" fontWeight={700}>Google Analytics 4 Property</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
          Select the GA4 property for traffic and conversion data.
        </Typography>

        {!isConnected ? (
          <Typography variant="body2" color="text.secondary">Connect Google first.</Typography>
        ) : isLoadingGa4 ? (
          <CircularProgress size={16} />
        ) : ga4Properties.length === 0 ? (
          <Alert severity="warning" sx={{ fontSize: 13 }}>No GA4 properties found.</Alert>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <Select
                value={selectedGa4}
                onChange={(e) => onSelectedGa4Change(e.target.value)}
                displayEmpty
                sx={{ fontSize: 14 }}
                size="small"
              >
                <MenuItem value="">None</MenuItem>
                {ga4Properties.map((prop) => (
                  <MenuItem key={prop.propertyId} value={prop.propertyId} sx={{ fontSize: 13 }}>
                    {prop.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {project.ga4PropertyId && selectedGa4 === project.ga4PropertyId && (
              <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 14 }} /> Currently using {project.ga4PropertyName}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* WordPress Connection */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <LanguageIcon sx={{ fontSize: 18, color: '#21759B' }} />
          <Typography variant="subtitle2" fontWeight={700}>WordPress Connection</Typography>
          {project.wpConnectedAt && (
            <Chip label="Connected" size="small" color="success" sx={{ fontSize: 10, height: 20 }} />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
          Connect your WordPress site to apply AI suggestions directly — update title tags, meta descriptions, and content without leaving SEOx.
        </Typography>

        {project.wpConnectedAt && project.wpSiteUrl ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2" fontWeight={500}>{project.wpSiteUrl}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">SEO Plugin:</Typography>
              <Chip
                label={project.wpSeoPlugin === 'yoast' ? 'Yoast SEO' : project.wpSeoPlugin === 'rankmath' ? 'RankMath' : 'None detected'}
                size="small"
                sx={{ fontSize: 10, height: 20 }}
                color={project.wpSeoPlugin !== 'none' ? 'primary' : 'default'}
              />
              <Typography variant="caption" color="text.secondary">User:</Typography>
              <Typography variant="caption" fontWeight={500}>{project.wpUsername}</Typography>
            </Box>
            {/* Bridge plugin install */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                SEOx Bridge Plugin
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: 11 }}>
                Required to update RankMath/Yoast SEO fields from SEOx. Install once — takes 30 seconds.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="contained"
                  href="/plugins/seox-wp-bridge.zip"
                  download
                  sx={{ backgroundColor: '#21759B', '&:hover': { backgroundColor: '#1a5f7a' }, textTransform: 'none', fontSize: 11 }}
                >
                  1. Download Plugin
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  href={`${project.wpSiteUrl}/wp-admin/plugin-install.php?tab=upload`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: 'none', fontSize: 11 }}
                >
                  2. Upload in WordPress
                </Button>
              </Box>
            </Box>

            {/* Sync pages */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                Sync Pages
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: 11 }}>
                Pull all published posts and pages from WordPress into SEOx. This populates the target page dropdown on keyword detail pages.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={onSyncWpPages}
                  disabled={isSyncingWpPages}
                  sx={{ backgroundColor: '#21759B', '&:hover': { backgroundColor: '#1a5f7a' }, textTransform: 'none', fontSize: 11 }}
                >
                  {isSyncingWpPages ? 'Syncing...' : 'Sync Pages from WordPress'}
                </Button>
                {wpPagesSynced != null && (
                  <Typography variant="caption" color="success.main" sx={{ fontSize: 11 }}>
                    {wpPagesSynced} pages synced
                  </Typography>
                )}
              </Box>
            </Box>

            <Button
              size="small"
              color="error"
              onClick={onDisconnectWordPress}
              sx={{ textTransform: 'none', fontSize: 12, mt: 2 }}
            >
              Disconnect WordPress
            </Button>
          </Box>
        ) : (
          <Box>
            <TextField
              fullWidth
              label="WordPress Site URL"
              placeholder="https://yoursite.com"
              value={wpSiteUrl}
              onChange={(e) => onWpSiteUrlChange(e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              label="WordPress Username"
              placeholder="admin"
              value={wpUsername}
              onChange={(e) => onWpUsernameChange(e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              label="Application Password"
              placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
              value={wpAppPassword}
              onChange={(e) => onWpAppPasswordChange(e.target.value)}
              size="small"
              type="password"
              sx={{ mb: 1.5 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: 11 }}>
              Generate an Application Password in WordPress: Users &rarr; Profile &rarr; Application Passwords. Your site must use HTTPS.
            </Typography>
            {wpConnectionError && (
              <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{wpConnectionError}</Alert>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={onConnectWordPress}
              disabled={isConnectingWp || !wpSiteUrl || !wpUsername || !wpAppPassword}
              sx={{ backgroundColor: '#21759B', '&:hover': { backgroundColor: '#1a5f7a' }, textTransform: 'none', fontSize: 12 }}
            >
              {isConnectingWp ? 'Connecting...' : 'Connect WordPress'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Save */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaving}
          sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' }, textTransform: 'none' }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={onDelete}
          sx={{ textTransform: 'none' }}
        >
          Delete Project
        </Button>
      </Box>
    </Box>
  );
};

export default SEOxProjectSettingsComponent;
